'use client'

import { useTranslations } from '@/lib/next-shim'
import type { SubmitHandler } from 'react-hook-form'

import { Box, Stack } from '@mui/material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'

import { useGetMyInfosQuery } from '@/core/graphql/hooks'
import { AttendingPhysician } from '@/core/graphql/types'
import { ControlledSelectField, ControlledTextField } from '@/shared/ui/rhform-controls'
import { useCustomFormWithReset } from '@/hooks/useCustomFormWithReset'

import { stripEmptyStrings } from '../lib/stripEmptyStrings'
import { treatingDoctorFormData, getAttendingPhysicianDefaultValues, treatingDoctor } from '../model/profileForms'
import { useProfileSave } from '../model/useProfileSave'
import { ProfileAccordion } from './ProfileAccordion'
import { SaveButton } from './SaveButton'

export function PrimaryCareSection() {
  const t = useTranslations()
  const { data } = useGetMyInfosQuery()
  const patient = data?.myInfos
  const [saveProfile] = useProfileSave()

  const { control, handleSubmit } = useCustomFormWithReset<treatingDoctorFormData, AttendingPhysician>(
    patient?.attendingPhysician,
    getAttendingPhysicianDefaultValues,
    treatingDoctor
  )

  const onSubmit: SubmitHandler<treatingDoctorFormData> = async (formData) => {
    const { name, surname, ...rest } = formData
    await saveProfile({
      attendingPhysician: stripEmptyStrings({ firstname: name, lastname: surname, ...rest }) as { firstname: string; lastname: string },
    })
  }

  return (
    <ProfileAccordion icon={<AccountCircleOutlinedIcon sx={{ color: 'primary.dark' }} />} title={t('page.AccountSettings.profile.primaryCare')}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <ControlledSelectField<treatingDoctorFormData>
            name="gender"
            label={t('field.gender.label')}
            control={control}
            options={[
              { value: 'M', label: 'M' },
              { value: 'F', label: 'F' },
              { value: 'Autre', label: 'Autre' },
            ]}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <ControlledTextField<treatingDoctorFormData> name="name" label={t('field.name.label')} control={control} />
            <ControlledTextField<treatingDoctorFormData> name="surname" label={t('field.lastname.label')} control={control} />
            <ControlledTextField<treatingDoctorFormData> name="phone" label={t('field.phone.label')} type="tel" control={control} />
            <ControlledTextField<treatingDoctorFormData> name="email" label={t('field.email.label')} type="email" control={control} />
          </Box>
          <Box sx={{ pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <SaveButton label={t('button.saveChanges.label')} />
          </Box>
        </Stack>
      </Box>
    </ProfileAccordion>
  )
}
